import React, { useRef, useState, useEffect, useContext } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Container, Accordion, Grid, TextField, Select
  , Switch, InputTable, Popover
  , Centralizer, ChangeDialog, Button, DataTable,
  Group, ErrorMessage, Dialog, DialogDataType
}
  from "../components"
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { areIdentical, cn, deepCopyOf, schemaHandler, logger, sleep } from "../utils"
import { useLogout, useJSONState, useTranslate, useStaticData } from '../hooks';
import { ShipmentType, shipmentSchema } from '../types/Shipments';
import { isRequired, dialog } from '../utils';
import { Schema, ZodError, set } from 'zod';
import { GoodslineType } from '../types/Goodsline';
import { DocumentType } from '../types/Document';
import { AxiosError } from 'axios';
import { add, format } from 'date-fns';
import { FaBars, FaCircleExclamation, FaFile, FaFilePdf, FaPrint, FaEye } from "react-icons/fa6";
import { toast } from 'sonner';
import { AddressType, DeliveryTypeType, ItemlineType, Shipment_NeededFieldsType } from '../types';
import { useWebSocket } from '../hooks'


import "../assets/style/Shipments.css" // 
import { AddressRegisterType } from '../types/Address';

function Shipment() {
  const debug = false

  const { shipmentId } = useParams();
  const api = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  let delayTime:number = location?.state?.delayTime

  const [changed, setChanged] = useState(false) //Checks if form has changed
  const submitForm = useRef<HTMLFormElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const { staticData } = useStaticData()
  const { t, tZod } = useTranslate();
  const [deliveryTypes, setDeliveryTypes] = useState<DeliveryTypeType[]>([])
  const [ addresses, setAddresses ] = useState<AddressRegisterType[]>([])
  const [originalShipment, setOriginalShipment] = useState<ShipmentType>(); //The original Shipment
  const [shipment, setProp, setShipment] = useJSONState<ShipmentType | undefined>() //The shown and maybe changed shipment
  const [validationErrors, setValidationErrors] = useState<ZodError<ShipmentType> | null>(null)

  const [ deliveryTerms, setDeliveryTerms ] = useState<{value: string,name: string, freeTextPH?: string}[] | undefined>()

  const [ neededFields, setNeededFields ] = useState<{
      [field: string]: {
          name: string;
          required: boolean;
          type: "text" | "number" | "switch" | "select" | "datalist";
          min?: number;
          max?: number;
          decimals?: number;
          options?: string;
      };
  } | undefined>()

  const printColors = new Map([
    [10, 'bg-gray-300 bg-transparent'],
    [20, 'bg-yellow-300 bg-transparent'],
    [22, 'bg-red-300 bg-transparent'],
    [25, 'bg-red-300 bg-transparent'],
    [80, 'bg-green-300 bg-transparent'],
    [100, 'bg-orange-200 bg-transparent'],
    // Add more values as needed
  ]);

  const printColorsText = new Map([
    [10, 'text-gray-600 dark:text-gray-400'],
    [90, 'text-yellow-600 dark:text-200'],
    [22, 'text-red-600 dark:text-red-400'],
    [25, 'text-red-600 dark:text-red-400'],
    [80, 'text-green-600 dark:text-green-400'],
    [100, 'text-orange-600 dark:text-orange-400'],
    // Add more values as needed
  ]);

  const getStatusElementClass = (status: number) => {
    let c = 'bg-gray-200 text-gray-600'
    if(status === 10) c = 'bg-gray-200 text-gray-600 dark:bg-gray-200 dark:text-gray-600'
    else if (status === 20) c = 'bg-yellow-200 text-yellow-700 dark:bg-yellow-200 dark:text-yellow-600'
    else if (status === 50) c = 'bg-blue-300 text-blue-900 dark:bg-blue-200 dark:text-blue-600'
    else if (status === 70) c = 'bg-gray-500 text-gray dark:bg-gray-600 dark:text-gray-300'    
    else if (status === 80) c = 'bg-green-400 text-green-900 dark:bg-green-200 dark:text-green-600'
    else if (status === 90) c = 'bg-orange-300 text-orange-800 dark:bg-orange-200 dark:text-orange-600'
    else if (status === -99) c = 'bg-red-400 text-red-900 dark:bg-red-200 dark:text-red-600'

    return `status content-center border-none rounded font-medium text-center dark:opacity-90 ${c}`    
  }  

  const locked = (shipment?.status && shipment?.status > 10) as boolean

  useEffect(() => {
    if(shipment && !originalShipment) return setChanged(true)
    if(!shipment || !originalShipment) return setChanged(false);

    if(areIdentical(shipment, originalShipment, ["isBooked"]))
      setChanged(false)
    else  
      setChanged(true)

    setValidationErrors(null)
  }, [shipment, originalShipment])

  useEffect(() => {    
    if(!staticData) return
    let isMounted = true;
    const controller = new AbortController();

    getShipmentInfo()

    return () => {
      isMounted = false;
      controller.abort()
    }
  }, [shipmentId, staticData])

  useEffect(() => {    
    if(!staticData || !shipment) return console.log("setDeliveryTerms: staticData or shipment is missing");
    const tradeArea = getTradeArea();

    const deliveryType = deliveryTypes.find(dt => dt.id === shipment.deliveryType)

    if(!tradeArea || !deliveryType) return console.log("setDeliveryTerms: tradeArea or deliveryType is missing", {tradeArea, deliveryType})
    
    console.log(`setDeliveryTerms:`, {deliveryType, tradeArea})

    const csp = staticData.carriersuitesProducts[deliveryType.carrierSuiteId]?.[tradeArea]
      ?.find(csp => csp.value === deliveryType[tradeArea]?.value)

    if(!csp) return console.log("setDeliveryTerms: csp is missing", {csp, deliveryType, tradeArea})

    let nfs = csp.neededFields

    deliveryType[tradeArea]?.serviceCodes.map(serviceCode => {
      let moreNfs = csp.serviceCodes.find(sc => sc.value === serviceCode)?.neededFields
      nfs = {...nfs, ...moreNfs}
    })
    setNeededFields(nfs);

    setDeliveryTerms(staticData.deliveryTerms[csp.incotermVersion ?? "Incoterms 2020"])

  }, [shipment?.deliveryType, shipment?.addresses.receiver.countryCode])


  const miscButton = () => {
    return <div className='flex-1 flex justify-end mr-2 relative'>
      <Popover 
        options={[
          {name: t("internalInfo"), onClick: async () => {
            const r = await dialog.open({
              noteInternal:{type:"textArea", name:"Internal notes..", value:shipment?.noteInternal ?? ""},
            })
            if(!r) return Promise.resolve()
            setProp(r.noteInternal, "noteInternal")
          }},          
          {name: t("createShipmentCopy"), onClick:() => returnOrCopyShipment("copy")},  
          {name: t("createShipmentReturn"), onClick:() => returnOrCopyShipment("return")},
          ...(shipment?.status !== 90
            ? [{name: t("cancelShipment"), onClick:async() => {
              if (!(await dialog.open({}, {title:t("cancelShipmentInfo")}))) return Promise.resolve()
              saveShipment({status:90})
            }}]
            : []),
          ...(shipment?.status === 90
            ? [{name: t("openShipment"), onClick:() => {
              saveShipment({status:10})
            }}]
            : []),          
        ]}
      >
        <FaBars />
      </Popover>
    </div>
  }

  const getShipmentInfo = async () => {
    try {
      const response = await api.get({
        endpoint: "/shipment/" + (shipmentId ?? "new"), 
        mount:{ 
          // Add a delay in the request if we're coming from Created Return / Copy
          skeleton:(delayTime && delayTime > (new Date).getTime() ? 800 : true)
        }
      })      

      console.log({data:response.data})

      let shipmentData: ShipmentType = response?.data?.shipmentData
      let addressRegister: AddressType[] = response?.data?.addressRegister.addresses
      let deliveryTypes: DeliveryTypeType[] = response?.data?.deliveryTypes
      
      if(shipmentData){
        shipmentData.goodsLines = shipmentData.goodsLines ?? []
        shipmentData.documents = shipmentData.documents ?? []
        shipmentData.itemLines = shipmentData.itemLines ?? []

        setShipment(shipmentData);
        setOriginalShipment(shipmentData)
      }else{
        shipmentData = {
          shipmentId: "new",
          status: 0,
          receiverPays: false,
          addresses: {
            receiver: {
              countryCode: staticData?.warehouse.countryCode
            }
          },
          currency: staticData?.warehouse.currency,
          goodsLines: [{type:"PC"}] as GoodslineType[],
          documents: [] as DocumentType[],
          itemLines: [] as ItemlineType[],
          isInbound: false,
          isBooked: false,
          bookingType: "NONE",
          dispDate: format(new Date(), 'yyyy-MM-dd'),
        } as ShipmentType
        setShipment(shipmentData);
      }     
      console.log({addressRegister})
      setAddresses(addressRegister ?? [])
      setDeliveryTypes(deliveryTypes ?? [])
    } catch (e) {
      if (e?.response?.status === 404) navigate("/shipments", { state: { dialog: { title: "Felaktig länk", message: "Sändningen existerar inte", type: "delete" } } })
      else {
        console.error(e)
        // logout()
      }
    }
  }

  const checkZipCode = async (zipCode: string, countryCode: string) => {
    if (zipCode.length < 3) return
    try{
      const response = await api.get({
        endpoint: "/common/checkzipcode",
        params: { zipCode, countryCode },
        mount: null
      })
      if (response.data) {
        if (response.data.valid)
          setProp(response.data.data?.city, 'city')
      }
    }catch(e){
      console.error(e)
    }    
  }

  const getTradeArea = () => staticData?.countries.find(c => c.value === shipment?.addresses.receiver.countryCode)?.tradeArea;

  const isDomestic = () => {
    let senderCC: string | undefined
    let receiverCC: string | undefined

    senderCC = shipment?.addresses?.sender?.countryCode ?? staticData?.warehouse.countryCode
    receiverCC = shipment?.addresses?.receiver?.countryCode

    if(!senderCC && shipment?.isInbound === false){
      senderCC = staticData?.warehouse.countryCode
    }    

    if(!senderCC || !receiverCC) return undefined
    return senderCC === receiverCC
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saveShipment()
  }

  const returnOrCopyShipment = async (type: "return" | "copy") => {
    try{
      const response = await api.put({
        endpoint: `/shipment?type=${type}&shipmentId=${shipmentId}`,
        mount:{loadingText: t("saving")}
      })
      const openShipment = await dialog.open({}, {title:t("shipmentCreatedAskOpen")})
      const delayTime = (new Date).getTime() + 500;
      if(openShipment) navigate(`/shipment/${response.data.shipmentId}`, {
        state: {delayTime}
      })
    }
    catch(e){
      console.error(e)
    }
  }

  const saveShipment = async (shipmentData?: Partial<ShipmentType>) => {    
    try{  
      if (locked) return
      
      let body;

      body = shipmentData || deepCopyOf(shipment)
      
      if(!body) return

      if(!shipmentData){
        const result = shipmentSchema.safeParse(body)  
        
        if (!result.success) {
          console.error("Failed saving",result.error)
          setValidationErrors(tZod(result.error))
          return false
        }
      }
      let response = {} as any;

      const data = shipmentData ? shipmentData : // shipmentData was provided
        originalShipment?.shipmentId ? schemaHandler.getDifferences(originalShipment, body, shipmentSchema): // Means it's an update
        schemaHandler.removeReadonlys(body, shipmentSchema) // Means it's a new one

      data.shipmentId = body.shipmentId || shipmentId || "new"

      console.log({data, body, originalShipment})

      response = await api.put({
        endpoint: `/shipment/${data.shipmentId}`,
        data,
        mount:{loadingText: t("saving")}
      })

      if(!response?.data?.shipmentId) {
        toast.error(t("errorTexts.unableToSave"))
        return
      }

      if(originalShipment?.shipmentId) toast.success(t("saved"))

      // We did an update)
      if(originalShipment?.shipmentId && originalShipment.shipmentId !== "new" && data.shipmentId === shipment?.shipmentId){
        const newShipmentBody = {...deepCopyOf(shipment), ...shipmentData}
        setOriginalShipment(newShipmentBody) 
        setShipment(newShipmentBody)
      }
      else navigate(`/shipment/${response.data.shipmentId}`) // We created a new one or copy etc
    }catch (err) {
      console.error(err)
    }
  }

  const printDocuments = async (docGUID?:string|undefined|null, preview:boolean=false) => {    
    if (submitForm.current?.checkValidity()) {
      try {
        await api.post({
          endpoint: `/shipment/${shipmentId}/print`,
          data: {docGUID, preview},
          mount: {
            loadingText: t("printing"),
            onLoadText: {text: t("sentToPrintClient"), type:"info"} 
          }
        }, )
      } catch (err) {
        console.error(err)
      }
      await getShipmentInfo()
    } else {
      submitButtonRef.current?.click();
    }    
  }

  const bookShipment = async () => {
    if (submitForm.current?.checkValidity()) {
      try{
        await api.post({
          endpoint: `/shipment/${shipmentId}/book`,
          mount: {
            loadingText: t("booking"),
            onLoadText: t("booked")
          }
        })
      }catch(e){
        console.error(e)        
      }
      await getShipmentInfo()
    } else {
      submitButtonRef.current?.click();
    }
  }

  const shipmentDocuments = () => {
    return <>
      {shipment?.documents?.map((d, i) => {
        return <div className='document'>
          <div className={`print ${cn(printColors.get(d.printStatus ?? 10))}`}>
            <FaPrint style={{marginRight:".5rem"}} className={cn(printColorsText.get(d.printStatus ?? 10))}/>
            <div className={cn(printColorsText.get(d.printStatus ?? 10))}
              onClick={() => printDocuments(d.docGUID, false)}
              style={{marginRight:"6px"}}
            >
              {t(d.documentName ?? "")}
            </div>
          </div>
          <div className={`preview ${cn(printColors.get(d.printStatus ?? 10))}`} 
            onClick={() => printDocuments(d.docGUID, true)}
          ><FaFilePdf className={cn(printColorsText.get(d.printStatus ?? 10))}/>
          </div>
        </div>
      })}
    </>
  }  

  const incotermFreeTextElement = () => {
    const freeText = deliveryTerms?.find(dt => dt.value === shipment?.combiterms)?.freeTextPH

    if(!freeText) return <></>

    return <TextField
        value={shipment?.incotermsFreetext}
        onChange={(e) => setProp(e.target.value, "incotermsFreetext")}
      >{t(freeText, undefined, freeText)}</TextField>
  }

  return (
    <>
      <Centralizer
        style={{
          position: "fixed",
          top: "50px",
          bottom: "0px",
          overflow: "auto",
          alignItems: "start",
          width: "100vw"
        }}
      >
        <Container style={{
          width: "clamp(1000px, 80%, 80vw)",
          maxHeight: "unset",
          marginBottom: "3rem"
        }}>

         {((originalShipment && originalShipment.validationError)) ? 
          <ErrorMessage>{originalShipment?.validationError}</ErrorMessage>
          : <></>
        } 
          <form onSubmit={(e) => handleOnSubmit(e)} ref={submitForm}>
            <Container>
              {shipmentId ? (
                <Grid>
                  { !shipment?.isInbound ? <></>:
                    <TextField
                      value={shipment.addresses?.sender?.name}
                      readOnly={true}
                    >{t("sender")}</TextField>
                  }
                  <TextField className={`w-auto ${cn(getStatusElementClass(shipment?.status ?? 10))}`} readOnly={true} disabled={true} value={t(`ta_statuses.${shipment?.status}`)} >{t("status")}</TextField>
                  <Button _type="button" onClick={() => printDocuments()}
                    onHover={shipmentDocuments()}  
                    disabled={shipment?.bookingType === "WEBSERVICE" && !shipment?.isBooked}
                    title={t("printAllDocumentsByPressingMe")}
                  >{t("printDocuments")}</Button>
                  {shipment?.bookingType === "NONE" ? <></> :
                    (shipment?.status !== 90 && (!shipment?.isBooked || deliveryTypes.find(dt => dt.id === shipment.deliveryType)?.bookingUpdateable)) ?
                    <Button _type="button" onClick={() => bookShipment()}>{t(shipment?.isBooked ? "updateBooking" : "bookBooking")}</Button>
                    : 
                    <Button _type="button" disabled={true}>{t("booked")}</Button>                    
                  }                  
                  {(shipment?.isBooked && shipment?.status !== 90 && deliveryTypes.find(dt => dt.id === shipment.deliveryType)?.bookingCancelable) ?
                    <Button _type="button" variant='delete' onClick={bookShipment}>{t("cancelBooking")}</Button>
                    : <></>
                  }
                  <p title={t("fieldInfo.shipmentId")}>{t("shipmentId")}: {shipmentId}</p>
                  { (shipment?.trackingLink || shipment?.trackingNumber) ? (<p>{t("trackingNo")}: {
                        shipment?.trackingLink ? <a className="tracking-number-link" title={t("track")} href={shipment.trackingLink} target='_blank'>{shipment.trackingNumber ?? t("track")}</a>: 
                        shipment?.trackingNumber ? <span>{shipment.trackingNumber}</span> : t("none")  
                      }</p>)
                      : <></>
                  }
                  
                  <button type="submit" style={{ display: "none" }} ref={submitButtonRef} ></button>


                  {miscButton()}
                </Grid>
              ) : (<></>)}

              <Accordion label={t("receiverAddress")} defaultOpen={true}>
                <Group label={t("receiverAddress")}>
                  <Container className='pb-0'>             
                    <Grid>
                      <Select 
                        label={t("receiver")}
                        data={addresses}
                        valueKey='addressId'
                        value={shipment?.addresses.receiver.name}
                        isDatalist={true}
                        readOnly={locked}
                        onChange={(e) => {
                          let address = addresses.find(a => a.addressId === e.target.value) 
                                        
                          console.log({address, shipment, addresses})

                          if(address === undefined){
                            setProp(e.target.value, "addresses", "receiver", "name")
                            return
                          }
                          
                          setShipment((prev: ShipmentType | undefined) => {
                            if(!prev) return
                            let newShipment = deepCopyOf(prev);
                            newShipment.addresses.receiver = address as AddressRegisterType
                            newShipment = {...newShipment, ...address?.prefill}

                            console.log({newShipment})

                            return newShipment as ShipmentType
                          })                          
                        }}
                        error={validationErrors?.errors.find(e => e.path[2] === "name")?.message}
                      />
                      <TextField
                        required={true}
                        value={shipment?.addresses.receiver.street1}
                        onChange={(e) => setProp(e.target.value, "addresses", "receiver", 'street1')}
                        error={validationErrors?.errors.find(e => e.path[2] === "street1")?.message}
                        readOnly={locked}
                      >{t("street1")}</TextField>
                      <TextField
                        required={false}
                        value={shipment?.addresses.receiver.street2}
                        onChange={(e) => setProp(e.target.value, "addresses", "receiver", 'street2')}
                        error={validationErrors?.errors.find(e => e.path[2] === "street2")?.message}
                        readOnly={locked}
                      >{t("street2")}</TextField>
                      <TextField
                        required={true}
                        value={shipment?.addresses.receiver.zipCode}
                        onChange={(e) => setProp(e.target.value, "addresses", "receiver", 'zipCode')}
                        onBlur={(e) => checkZipCode(e.target.value, shipment?.addresses.receiver.countryCode || "")}
                        error={validationErrors?.errors.find(e => e.path[2] === "zipCode")?.message}
                        readOnly={locked}
                      >{t("zipCode")}</TextField>
                      <TextField
                        required={true}
                        value={shipment?.addresses.receiver.city}
                        onChange={(e) => setProp(e.target.value, "addresses", "receiver", 'city')}
                        error={validationErrors?.errors.find(e => e.path[2] === "city")?.message}
                        readOnly={locked}
                      >{t("city")}</TextField>
                      <Select
                        required={true}
                        label={t("country")}
                        data={staticData?.countries ?? []}
                        value={shipment?.addresses.receiver.countryCode}
                        onChange={(e) => { setProp(e.target.value, "addresses", "receiver", 'countryCode') }}
                        error={validationErrors?.errors.find(e => e.path[2] === "countryCode")?.message}
                        readOnly={locked}
                      ></Select>                    
                    </Grid>
                    <Group label={t("contactInfo")}>
                      <Grid>
                        <TextField
                          value={shipment?.addresses.receiver.contact?.name}
                          onChange={(e) => setProp(e.target.value, "addresses", "receiver", "contact", 'name')}
                          error={validationErrors?.errors.find(e => e.path[3] === "name")?.message}
                          required={!!neededFields?.contactName}
                          readOnly={locked}
                        >{t("contactName")}</TextField>
                        <TextField                          
                          _type="tel"
                          value={shipment?.addresses.receiver.contact?.phoneNo}
                          onChange={(e) => setProp(e.target.value, "addresses", "receiver", "contact", 'phoneNo')}
                          error={validationErrors?.errors.find(e => e.path[3] === "phoneNo")?.message}
                          required={!!neededFields?.contactPhone}
                          readOnly={locked}
                        >{t("contactPhone")}</TextField>
                        <TextField
                          _type="text"
                          value={shipment?.addresses.receiver.contact?.email}
                          onChange={(e) => setProp(e.target.value != "" ? e.target.value : undefined, "addresses", "receiver", "contact", 'email')}
                          error={validationErrors?.errors.find(e => e.path[3] === "email")?.message}
                          required={!!neededFields?.contactEmail}
                          readOnly={locked}
                        >{t("contactEmail")}</TextField>
                      </Grid>
                    </Group>
                  </Container>
                </Group>                
              </Accordion>
              <Accordion label={t("transport")} defaultOpen={true}>
                <Container className='pb-0'>
                  <Grid>
                    <Select
                      data={deliveryTypes}
                      nameKey='name'
                      valueKey='id'
                      blankOption={true}
                      label={t("deliveryType")}
                      required={isRequired("deliveryType", shipmentSchema)}
                      value={shipment?.deliveryType || ""}
                      onChange={(e) => setProp(e.target.value, "deliveryType")}
                      error={validationErrors?.errors.find(e => e.path[0] === "deliveryType")?.message}
                      readOnly={locked}
                    />
                    <TextField
                      required={isRequired('dispDate', shipmentSchema)}
                      _type={neededFields?.dispTime ? "datetime-local" : "date"}
                      value={!shipment?.dispDate ? undefined : format(shipment?.dispDate, neededFields?.dispTime ? "yyyy-MM-dd HH:mm": "yyyy-MM-dd")}
                      onChange={(e) => setProp(format(e.target.value, neededFields?.dispTime ? "yyyy-MM-dd HH:mm": "yyyy-MM-dd"), 'dispDate')}
                      error={validationErrors?.errors.find(e => e.path[0] === "dispDate")?.message}
                      readOnly={locked}
                    >{t("dispDate")}</TextField>
                    {!neededFields?.arrivalDate ? <></> :
                      <TextField
                        required={neededFields.arrivalDate.required}
                        _type={neededFields?.arrivalTime ? "datetime-local" : "date"}
                        value={!shipment?.arrivalDate ? undefined : format(shipment?.arrivalDate, neededFields?.dispTime ? "yyyy-MM-dd HH:mm": "yyyy-MM-dd")}
                        onChange={(e) => setProp(format(e.target.value, neededFields?.arrivalTime ? "yyyy-MM-dd HH:mm": "yyyy-MM-dd"), 'arrivalDate')}
                        error={validationErrors?.errors.find(e => e.path[0] === "arrivalDate")?.message}
                        readOnly={locked}
                      >{t("arrivalDate")}</TextField>
                    }
                    <TextField
                      required={isRequired('orderNumber', shipmentSchema)}
                      _type="text"
                      value={shipment?.orderNumber}
                      onChange={(e) => setProp(e.target.value, 'orderNumber')}
                      error={validationErrors?.errors.find(e => e.path[0] === "orderNumber")?.message}
                      readOnly={locked}
                    >{t("orderNumber")}</TextField>
                    { isDomestic() ? 
                    <>
                      <Switch
                        label={t("receiverPays")}
                        checked={shipment?.receiverPays}
                        onChange={(e) => setProp(e.currentTarget.checked, 'receiverPays')}
                        disabled={locked}
                      ></Switch>
                      {!shipment?.receiverPays ? <></> :
                        <TextField
                          required={true}
                          value={shipment?.customerNumber}
                          onChange={(e) => setProp(e.target.value, 'customerNumber')}
                          error={validationErrors?.errors.find(e => e.path[0] === "customerNumber")?.message}
                          readOnly={locked}
                        >{t("customerNumber")}</TextField>
                      }   
                    </>:<></>
                    }                                     
                    <Select 
                      label={t("incoterm")}
                      title={t("fieldInfo.incoterms")}
                      required={!isDomestic()}
                      value={shipment?.combiterms ?? ""}
                      onChange={(e) => setProp(e.target.value, 'combiterms')}
                      data={deliveryTerms ?? []}
                      readOnly={locked}
                    />
                    { incotermFreeTextElement() }                    
                    <Button
                      _type='button'
                      onClick={async () => {
                        const r: Partial<ShipmentType> | null = await dialog.open({
                          msgToFW1: {type:"text", name:"", max:70, value: shipment?.msgToFW1, readOnly: locked},
                          msgToFW2: {type:"text", name:"", max:70, value: shipment?.msgToFW2, readOnly: locked},
                          msgToFW3: {type:"text", name:"", max:70, value: shipment?.msgToFW3, readOnly: locked},
                        }, {hideCancelButton: true, title: t("messagesToCarrier")})
                        if(!r) return Promise.resolve();

                        setShipment(s => {return {...s, ...r} as ShipmentType})
                      }}
                    >{t("messagesToCarrier")+"..."}</Button>
                    {!neededFields?.insuranceValue ? <></>:
                      <Group label={t("insurance")}>
                        <Grid>
                          <Select 
                            value={shipment?.insurance?.currency ?? ""} 
                            data={staticData?.currencies ?? []} 
                            label={t("currency")}
                            required={!!neededFields?.insuranceCurrency}
                            onChange={(e) => setProp(e.currentTarget.value, "insurance", "currency")}
                            readOnly={locked}
                          />
                          <TextField
                            _type='number'
                            value={shipment?.insurance?.value}
                            required={!!neededFields?.insuranceValue}
                            onChange={(e) => setProp(parseFloat(e.target.value), "insurance", "value")}
                            readOnly={locked}
                          >{t("value")}</TextField> 
                        </Grid>
                      </Group>
                    }                                        
                  </Grid>
                </Container>
              </Accordion>
              <Accordion label={t("goods")} defaultOpen={true}>
                <Container>
                  <Grid>
                    <Select 
                      value={shipment?.currency ?? ""} 
                      data={staticData?.currencies ?? []} 
                      label={t("currency")}
                      readOnly={locked}
                      onChange={(e) => setProp(e.currentTarget.value, "currency")}
                    />
                  </Grid>
                </Container>
                <Accordion label={t("goodsLines")} defaultOpen={true}>
                  <InputTable
                    data={shipment?.goodsLines as any[] || []}
                    zodErrors={validationErrors as ZodError<ShipmentType>}
                    headers={{
                      quantity: { type: "number", required: true, readOnly: locked},
                      type: {
                        type: "text",
                        required: true,
                        options: [
                          { name: t("parcel"), value: "PC" },
                          { name: t("pallet"), value: "PX" },
                          { name: t("EURpallet"), value: "PE" }
                        ],
                        readOnly: locked 
                      },
                      weight: { type: "number", required: true, adornment: "kg", readOnly: locked, min:0, decimals:3, max: 9999999},
                      description: { type: "text", readOnly: locked, className:"shipmentGoodsDescription" },
                      length: { type: "number", adornment: "cm", readOnly: locked, min:0, decimals:0, max: 9999},
                      width: { type: "number", adornment: "cm", readOnly: locked, min:0, decimals:0, max: 9999},
                      height: { type: "number", adornment: "cm", readOnly: locked, min:0, decimals:0, max: 9999},
                      volume: { type: "number", adornment: "M3", readOnly: locked, min:0, decimals:3, max: 999},
                      loadmeters: { type: "number", min:0, decimals:2, max:999, readOnly: locked },
                      trackingNumbers: { type:"text", name: "trackingNos", jsx: (value) => <Button  _type='button' onClick={() => {
                        if(!value) return
                        const trackingNumbers = Array.isArray(value) ? value : [value]

                        dialog.open({
                          trackingNumbers: { type: "textArea", name: "", value: trackingNumbers.join("\n") }
                        }, { title: t("trackingNos"), hideCancelButton: true })
                      }}>{t("view")}</Button>, readOnly: true }
                    }}
                    addable={!locked}
                    deleteable={locked ? false: 1}
                    setProp={setProp}
                    propPath="goodsLines"
                  />
                </Accordion>
                <Accordion label={t("itemLines")} defaultOpen={false}>
                  <InputTable 
                    data={shipment?.itemLines as any[] || []}
                    zodErrors={validationErrors as ZodError<ItemlineType>}
                    headers={{
                      itemNo: { type:"text", required: true, readOnly: locked },
                      supplierItemNo: { type:"text", required: false, readOnly: locked },
                      description: { type:"text", required: true, readOnly: locked, className:"shipmentItemsDescription" },
                      pickedQuantity: { type:"number", required: true, min:0, readOnly: locked },
                      orderedQuantity: { type:"number", required: true, min:1, readOnly: locked },
                      price: { type:"number", min:0, readOnly: locked, decimals:2, adornment:"SEK" },
                      hsTariffNumber: { type:"text", readOnly: locked },
                      countryOfOrigin: { type:"select", options: staticData?.countries, readOnly: locked, className:"shipmentItemsCountry"},
                      category: { type:"text", readOnly: locked },
                      weight: { type:"number", min:0, readOnly: locked, adornment:"kg" },
                    }}
                    addable={!locked}
                    deleteable={locked ? false: true}
                    setProp={setProp}
                    propPath="itemLines"
                  />
                </Accordion>                
              </Accordion>    
            </Container>
            <ChangeDialog show={changed}
              undo={(!shipmentId ? undefined : async () => {
                setShipment(originalShipment)
                setTimeout(() => setChanged(false), 10)
              })}
            />
          </form>
        </Container>
      </Centralizer>
              
      {debug && <pre className='-z-50 absolute left-1 top-24 font-mono text-xs' >
        {JSON.stringify(shipment, undefined, 2)}
      </pre>}
    </>
  )
}

export default Shipment